import {
  COL_PRODUCTION_REQUEST_LINES,
  COL_PRODUCTION_REQUESTS,
  COL_PRODUCTS,
} from '../config/collections'
import { pb } from '../pocketbase'
import { pocketbaseErrorMessage } from './pocketbaseErrorMessage'
import {
  buildProductionRequestCreateBody,
  buildProductionRequestLineCreateBody,
} from './productionRequestPayload'
import type { ProductionRequestRecord } from '../types/records'

export type CrmOrderLine = {
  productCode: string
  quantity: number
  plannedProduction?: string
  plannedShipManager?: string
  priority?: number
}

export type CrmOrderPayload = {
  externalId?: string
  clientCollection: string
  clientRecordId: string
  clientName: string
  receivedAt: string
  clientUrgent?: boolean
  lines: CrmOrderLine[]
}

export type CrmImportResult = {
  ok: boolean
  requestId?: string
  request?: ProductionRequestRecord
  errors: string[]
}

/** Включён ли импорт заказов CRM (webhook/cron — фаза 5). */
export function isCrmOrderImportEnabled(): boolean {
  return Boolean(import.meta.env.VITE_CRM_ORDER_IMPORT?.trim())
}

/**
 * Импорт заказа CRM → production_requests + lines.
 * Сопоставление по products.code; полная синхронизация webhook — позже.
 */
export async function importCrmOrder(payload: CrmOrderPayload): Promise<CrmImportResult> {
  const errors: string[] = []

  if (!payload.clientCollection?.trim() || !payload.clientRecordId?.trim()) {
    errors.push('Не указан клиент CRM')
  }
  if (!payload.lines?.length) {
    errors.push('Нет строк заказа')
  }
  for (const line of payload.lines ?? []) {
    if (!line.productCode?.trim()) errors.push('Пустой код продукта в строке заказа')
    if ((Number(line.quantity) || 0) <= 0) errors.push(`Некорректное кол-во для ${line.productCode}`)
  }
  if (errors.length) return { ok: false, errors }

  try {
    const request = await pb.collection(COL_PRODUCTION_REQUESTS).create<ProductionRequestRecord>(
      buildProductionRequestCreateBody({
        receivedAt: payload.receivedAt,
        clientCollection: payload.clientCollection,
        clientRecordId: payload.clientRecordId,
        clientName: payload.clientName,
        clientUrgent: payload.clientUrgent ?? false,
      }) as Omit<ProductionRequestRecord, 'id' | 'collectionId' | 'collectionName' | 'created' | 'updated'>,
    )

    let sort = 0
    for (const line of payload.lines) {
      sort += 10
      const code = line.productCode.trim()
      const products = await pb.collection(COL_PRODUCTS).getList(1, 1, {
        filter: `code="${code}"`,
      })
      if (!products.items.length) {
        errors.push(`Продукт не найден: ${code}`)
        continue
      }

      await pb.collection(COL_PRODUCTION_REQUEST_LINES).create(
        buildProductionRequestLineCreateBody(
          {
            request: request.id,
            product: products.items[0].id,
            quantity: line.quantity,
            sort,
            plannedProduction: line.plannedProduction,
            plannedShipManager: line.plannedShipManager,
          },
          line.priority ?? 3,
        ),
      )
    }

    if (errors.length) {
      return { ok: false, requestId: request.id, request, errors }
    }
    return { ok: true, requestId: request.id, request, errors: [] }
  } catch (e) {
    return { ok: false, errors: [pocketbaseErrorMessage(e, 'Ошибка импорта заказа')] }
  }
}

/** Заглушка для webhook: валидация без записи в БД. */
export function validateCrmOrderPayload(payload: CrmOrderPayload): string[] {
  const errors: string[] = []
  if (!payload.receivedAt) errors.push('Не указана дата заказа')
  if (!payload.clientName?.trim()) errors.push('Не указано имя клиента')
  if (!payload.lines?.length) errors.push('Нет строк заказа')
  return errors
}
